import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Target, Flame, NotebookPen, Sparkles, Film, Wind, FileText, Download } from "lucide-react";
import { getYoutubeThumb } from "@/lib/video";

export default function MyPlan() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [files, setFiles] = useState([]);
  const [filesError, setFilesError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const p = await api.get("/plans/me");
        setData(p.data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
      try {
        const f = await api.get("/files/me");
        setFiles(f.data || []);
      } catch (e) {
        setFilesError(formatApiError(e));
      }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Loading your plan...</div>;
  const plan = data?.plan || {};
  const practices = data?.practices || [];
  const videos = data?.videos || [];
  const goals = plan.goals || [];
  const notes = plan.session_notes || [];
  const hasContent = plan.welcome_message || plan.coach_notes || goals.length || plan.weekly_session_target || practices.length || videos.length || notes.length || files.length;

  const downloadFile = (fileId) => {
    const token = localStorage.getItem("epb_token");
    const url = `${api.defaults.baseURL}/files/${fileId}/download?auth=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-12 lg:py-16">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <p className="overline text-primary mb-2">My Plan</p>
        <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight" data-testid="myplan-greeting">
          Hello, {user?.name?.split(" ")[0]}.
        </h1>
        {plan.welcome_message ? (
          <p className="mt-4 text-lg text-foreground/80 max-w-3xl leading-relaxed" data-testid="myplan-welcome">{plan.welcome_message}</p>
        ) : (
          <p className="mt-4 text-muted-foreground max-w-2xl">Your coach hasn't published your personalised plan yet. Check back soon.</p>
        )}
      </motion.div>

      {!hasContent && (
        <Card className="mt-12 p-12 text-center">
          <Sparkles className="h-10 w-10 mx-auto text-primary/60" />
          <p className="mt-4 text-muted-foreground text-sm">Once Evan publishes your plan it will appear here — assigned practices, focus areas, video recommendations, files, and personalised notes after each 1:1 session.</p>
        </Card>
      )}

      {/* Goals + weekly target */}
      {(goals.length > 0 || plan.weekly_session_target > 0) && (
        <div className="grid lg:grid-cols-3 gap-6 mt-12">
          {goals.length > 0 && (
            <Card className="lg:col-span-2 p-7">
              <div className="flex items-center gap-2 mb-4">
                <Target className="h-4 w-4 text-primary" />
                <p className="overline text-primary">Your focus areas</p>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="myplan-goals">
                {goals.map((g, i) => (
                  <Badge key={i} variant="secondary" className="text-sm px-4 py-1.5">{g}</Badge>
                ))}
              </div>
            </Card>
          )}
          {plan.weekly_session_target > 0 && (
            <Card className="p-7">
              <div className="flex items-center gap-2 mb-4">
                <Flame className="h-4 w-4 text-primary" />
                <p className="overline text-primary">Weekly target</p>
              </div>
              <p className="font-display text-4xl font-semibold tracking-tight" data-testid="myplan-target">{plan.weekly_session_target}<span className="text-base text-muted-foreground font-normal"> sessions</span></p>
            </Card>
          )}
        </div>
      )}

      {/* Coach notes */}
      {plan.coach_notes && (
        <div className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <NotebookPen className="h-4 w-4 text-primary" />
            <p className="overline text-primary">From your coach</p>
          </div>
          <Card className="p-7 bg-secondary/30 border-secondary/50">
            <p className="text-foreground/85 leading-relaxed whitespace-pre-line" data-testid="myplan-coach-notes">{plan.coach_notes}</p>
          </Card>
        </div>
      )}

      {/* Shared files — always shown when client has any plan content, with empty state for visibility */}
      {(hasContent || files.length > 0) && (
        <div className="mt-14">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="h-4 w-4 text-primary" />
            <p className="overline text-primary">Shared with you</p>
          </div>
          <h2 className="font-display text-2xl font-semibold tracking-tight mb-6">Files from your coach</h2>
          {filesError ? (
            <Card className="p-6 border-destructive/40 bg-destructive/5">
              <p className="text-sm text-destructive" data-testid="myplan-files-error">Couldn't load your files: {filesError}</p>
            </Card>
          ) : files.length === 0 ? (
            <Card className="p-6 text-center">
              <p className="text-sm text-muted-foreground">No files shared yet. When your coach uploads documents, recordings, or worksheets for you, they'll appear here.</p>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {files.map((f) => (
                <Card key={f.id} className="p-5 flex items-center gap-4" data-testid={`myplan-file-${f.id}`}>
                  <div className="h-10 w-10 rounded-md bg-secondary/40 text-primary flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{f.original_filename}</p>
                    <p className="text-xs text-muted-foreground">
                      {(f.size / 1024).toFixed(1)} KB · {new Date(f.created_at).toLocaleDateString()}
                      {f.description ? ` · ${f.description}` : ""}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadFile(f.id)} data-testid={`myplan-file-download-${f.id}`}>
                    <Download className="h-3.5 w-3.5 mr-1" /> Download
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Assigned practices */}
      {practices.length > 0 && (
        <div className="mt-14">
          <div className="flex items-center gap-2 mb-1">
            <Wind className="h-4 w-4 text-primary" />
            <p className="overline text-primary">Your prescription</p>
          </div>
          <h2 className="font-display text-2xl font-semibold tracking-tight mb-6">Practices assigned to you</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {practices.map((p) => (
              <Link key={p.id} to={`/practice/${p.id}`}>
                <Card className="p-6 h-full hover:border-primary/40 transition-colors" data-testid={`myplan-practice-${p.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="secondary" className="text-xs">{p.category}</Badge>
                    <span className="text-xs text-muted-foreground">{p.duration_minutes} min</span>
                  </div>
                  <h3 className="font-display text-lg font-semibold tracking-tight">{p.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.purpose}</p>
                  <Button variant="ghost" size="sm" className="mt-4 -ml-3 text-primary">Start <ArrowRight className="ml-1 h-3 w-3" /></Button>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Assigned videos */}
      {videos.length > 0 && (
        <div className="mt-14">
          <div className="flex items-center gap-2 mb-1">
            <Film className="h-4 w-4 text-primary" />
            <p className="overline text-primary">Recommended viewing</p>
          </div>
          <h2 className="font-display text-2xl font-semibold tracking-tight mb-6">Videos for you</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((v) => {
              const thumb = v.thumbnail_url || getYoutubeThumb(v.url);
              return (
                <Card key={v.id} className="overflow-hidden hover:border-primary/40 transition-colors" data-testid={`myplan-video-${v.id}`}>
                  <Link to="/videos" className="block aspect-video bg-muted overflow-hidden">
                    {thumb ? (
                      <img src={thumb} alt={v.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-secondary/30">
                        <Film className="h-8 w-8 text-primary/50" />
                      </div>
                    )}
                  </Link>
                  <div className="p-5">
                    <Badge variant="secondary" className="text-xs mb-2">{v.category || "General"}</Badge>
                    <h3 className="font-display text-base font-semibold tracking-tight">{v.title}</h3>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Session notes timeline */}
      {notes.length > 0 && (
        <div className="mt-14">
          <p className="overline text-primary mb-2">1:1 session log</p>
          <h2 className="font-display text-2xl font-semibold tracking-tight mb-6">Your private session notes</h2>
          <div className="relative pl-6 space-y-6 border-l-2 border-secondary/60">
            {notes.map((n) => (
              <div key={n.id} className="relative" data-testid={`myplan-note-${n.id}`}>
                <span className="absolute -left-[31px] top-1.5 h-3 w-3 rounded-full bg-primary" />
                <p className="text-xs text-muted-foreground">{new Date(n.date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</p>
                {n.title && <h3 className="font-display text-lg font-semibold tracking-tight mt-1">{n.title}</h3>}
                <p className="mt-2 text-sm text-foreground/85 leading-relaxed whitespace-pre-line">{n.note}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
